import { useLingui } from '@lingui/react';
import { MATCH_SWITCH_CHECKLIST_TIMEOUT_MS, READINESS_EXIT_DURATION_MS } from 'modules/overlay-v2/domain/constants/readiness';
import {
  OverlayReadinessSignals,
  OverlayReadinessState,
  OverlayReadinessStepId,
  PreparationScreen,
} from 'modules/overlay-v2/domain/interfaces/readiness';
import { buildReadinessSteps } from 'modules/overlay-v2/domain/utils/build-readiness-steps';
import { resolvePreparationScreen } from 'modules/overlay-v2/domain/utils/resolve-preparation-screen';
import { selectGameInfoError } from 'modules/overlay-v2/store/game-info/selectors';
import { selectIsBroadcastInitialized, selectIsMatchAvailable } from 'modules/overlay-v2/store/overlay/selectors';
import {
  checklistEscalated,
  retryCancelled,
  retryRequested,
  retryScheduled,
  wentLive,
} from 'modules/overlay-v2/store/readiness/readiness';
import {
  selectAssetsTask,
  selectChronologyTask,
  selectDeviceTask,
  selectHasEverGoneLive,
  selectHasGoneLive,
  selectIsChecklistEscalated,
  selectMatchTimeTask,
  selectPreparationStartedAt,
  selectReadinessRetry,
  selectRetryAt,
  selectRetryToken,
  selectTemplateTask,
} from 'modules/overlay-v2/store/readiness/selectors';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import useOverlayRouteParams from 'modules/overlay-v2/ui/hooks/use-overlay-route-params';
import useRemoteConnectionStep from 'modules/overlay-v2/ui/hooks/use-remote-connection-step';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFetchAvailableFeature } from 'shared/hooks/fetchering/use-fetch-available-feature';
import { useAppDispatch, useAppSelector } from 'store/hooks';

export interface OverlayReadiness extends OverlayReadinessState {
  /** Повторить подготовку прямо сейчас, не дожидаясь автоматического повтора. */
  onRetry: () => void;
  /**
   * Что показывать поверх табло, пока матч готовится (`resolvePreparationScreen`):
   * чеклист при первом открытии, знак ImGame при переключении матча, ничего — в эфире.
   * Экран держится ещё `READINESS_EXIT_DURATION_MS` после готовности, чтобы успеть уйти
   * анимацией, а не смениться табло рывком.
   */
  preparationScreen: PreparationScreen;
  /**
   * Монтировать ли табло и панель.
   *
   * Это **не** `isReady`. `isReady` — мгновенное «прямо сейчас все сигналы сошлись», и
   * монтировать по нему нельзя: любая пересинхронизация, моргнувшая сеть или нажатая
   * оператором кнопка роняли его в ноль и пересоздавали канвы Fabric прямо в эфире.
   * `isOnAir` — однонаправленное «подготовка этого матча уже прошла», и снимается оно
   * только вместе со сменой матча.
   */
  isOnAir: boolean;
}

/**
 * Чеклист подготовки трансляции для оверлея и панели управления.
 *
 * Хук только собирает сигналы из тех мест, где они уже живут, — саму лестницу считает
 * чистая `buildReadinessSteps`. Своё состояние здесь минимально: три шага, которым
 * негде было храниться (журнал матча, время, картинки), лежат в слайсе `readiness`.
 */
function useOverlayReadiness(): OverlayReadiness {
  const dispatch = useAppDispatch();
  const { i18n } = useLingui();

  const { organizerId, error: routeError } = useOverlayRouteParams();

  const {
    data: feature,
    isLoading: isFeatureLoading,
    error: featureError,
    refetch: refetchFeature,
  } = useFetchAvailableFeature({ userTournamentId: organizerId });

  const isBroadcastInitialized = useAppSelector(selectIsBroadcastInitialized);
  const isMatchAvailable = useAppSelector(selectIsMatchAvailable);

  const device = useAppSelector(selectDeviceTask);
  const chronology = useAppSelector(selectChronologyTask);
  const matchTime = useAppSelector(selectMatchTimeTask);
  const assets = useAppSelector(selectAssetsTask);
  const template = useAppSelector(selectTemplateTask);
  const retry = useAppSelector(selectReadinessRetry);
  const retryToken = useAppSelector(selectRetryToken);

  const { championship, teams, isSynchronization, isSynchronize } = useGameInfo();
  const gameInfoError = useAppSelector(selectGameInfoError);
  const { activeTemplate, isLoaded: isTemplateLoaded } = useTemplates();
  const { boards } = useBoards();

  const remote = useRemoteConnectionStep(isMatchAvailable === true);

  const involvedPlayers = useMemo(() => {
    if (!teams) return undefined;
    const count = (members?: { isInvolved: boolean }[]) => members?.filter((member) => member.isInvolved).length ?? 0;
    return { first: count(teams.first.members), second: count(teams.second.members) };
  }, [teams]);

  const signals: OverlayReadinessSignals = useMemo(
    () => ({
      routeError,
      subscription: {
        isLoading: isFeatureLoading,
        error: featureError ? ((featureError as Error).message ?? String(featureError)) : undefined,
        hasObsIntegration: Boolean(feature?.OBS_INTEGRATION),
      },
      isBroadcastInitialized,
      device,
      isMatchAvailable,
      matchData: { isLoading: isSynchronization, isLoaded: isSynchronize, error: gameInfoError },
      involvedPlayers,
      chronology,
      matchTime,
      assets,
      template,
      championship: championship ? { name: championship.name, hasTemplate: Boolean(championship.overlayId) } : undefined,
      isTemplateLoaded,
      templateName: activeTemplate?.name,
      boardsCount: boards.length,
      remote,
      teamNames: teams ? { first: teams.first.name, second: teams.second.name } : undefined,
      retry,
    }),
    [
      routeError,
      isFeatureLoading,
      featureError,
      feature?.OBS_INTEGRATION,
      isBroadcastInitialized,
      device,
      isMatchAvailable,
      isSynchronization,
      isSynchronize,
      gameInfoError,
      involvedPlayers,
      chronology,
      matchTime,
      assets,
      template,
      championship,
      isTemplateLoaded,
      activeTemplate?.name,
      boards.length,
      remote,
      teams,
      retry,
    ],
  );

  const onRetry = useCallback(() => dispatch(retryRequested()), [dispatch]);

  // Подписи шагов переводятся внутри `buildReadinessSteps`: смена языка пересобирает чеклист.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const state = useMemo(() => buildReadinessSteps(signals), [signals, i18n.locale]);

  // Подписка живёт в react-query с бессрочным кэшем (`staleTime: Infinity, retry: false`),
  // поэтому её сбой сам не рассасывается: без явного `refetch` шаг оставался красным
  // навсегда, даже когда сеть уже вернулась и все запросы отвечали 200.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    refetchFeature();
  }, [retryToken, refetchFeature]);

  const hasGoneLive = useAppSelector(selectHasGoneLive);
  const hasEverGoneLive = useAppSelector(selectHasEverGoneLive);
  const isChecklistEscalated = useAppSelector(selectIsChecklistEscalated);

  useRetryDriver(state, useAppSelector(selectRetryAt), hasGoneLive);
  useExitDelay(state.isReady, hasGoneLive);
  useChecklistEscalation({ hasGoneLive, hasEverGoneLive, isChecklistEscalated, failedStepId: state.failedStepId });

  const isOnAir = state.isReady || hasGoneLive;

  // Таймаут переключения в решение не передаём: по его истечении `useChecklistEscalation`
  // поднимает латч `isChecklistEscalated`, и дальше экран держится на нём.
  const preparationScreen = resolvePreparationScreen({
    hasGoneLive,
    hasEverGoneLive,
    isChecklistEscalated,
    failedStepId: state.failedStepId,
    isTimedOut: false,
  });

  return useMemo(() => ({ ...state, onRetry, preparationScreen, isOnAir }), [state, onRetry, preparationScreen, isOnAir]);
}

/**
 * Переключение матча идёт под знаком ImGame, пока не случится одно из двух: упал шаг
 * подготовки или прошло `MATCH_SWITCH_CHECKLIST_TIMEOUT_MS` с начала подготовки. Тогда
 * выходит чеклист и остаётся до готовности — латч `isChecklistEscalated`.
 *
 * Отсчёт берётся из стора (`preparationStartedAt`), а не с монтирования хука: панель и
 * оверлей монтируются независимо, и перемонтирование не должно начинать ожидание заново.
 */
function useChecklistEscalation({
  hasGoneLive,
  hasEverGoneLive,
  isChecklistEscalated,
  failedStepId,
}: {
  hasGoneLive: boolean;
  hasEverGoneLive: boolean;
  isChecklistEscalated: boolean;
  failedStepId?: OverlayReadinessStepId;
}) {
  const dispatch = useAppDispatch();
  const preparationStartedAt = useAppSelector(selectPreparationStartedAt);

  // Первое открытие и так показывает чеклист, а в эфире экрана подготовки нет вовсе.
  const isQuietSwitch = hasEverGoneLive && !hasGoneLive && !isChecklistEscalated;

  useEffect(() => {
    if (isQuietSwitch && failedStepId) dispatch(checklistEscalated());
  }, [dispatch, isQuietSwitch, failedStepId]);

  useEffect(() => {
    if (!isQuietSwitch || preparationStartedAt === undefined) return;

    const remainingMs = Math.max(0, preparationStartedAt + MATCH_SWITCH_CHECKLIST_TIMEOUT_MS - Date.now());
    const timer = setTimeout(() => dispatch(checklistEscalated()), remainingMs);
    return () => clearTimeout(timer);
  }, [dispatch, isQuietSwitch, preparationStartedAt]);
}

/**
 * Автоповтор упавшего шага: назначает попытку, ведёт её по таймеру и снимает, когда шаг
 * починился сам. Живёт рядом с чеклистом, а не в загрузчике, потому что упасть может
 * что угодно — подписка, оформление, данные матча, — а видит их все только чеклист.
 *
 * Работает только до выхода в эфир. Повтор — операция не бесплатная: он передёргивает
 * `setIsBroadcastInitialized(false)`, переподписывает realtime-каналы и заново тянет
 * данные матча, журнал, время и все картинки. Делать это посреди эфира нельзя, тем более
 * молча — чеклиста, который бы это объяснил, там уже нет. В эфире восстановлением
 * занимаются штатные механизмы: realtime-подписка и опрос журнала раз в 3 секунды.
 */
function useRetryDriver(state: OverlayReadinessState, retryAt: number | undefined, hasGoneLive: boolean) {
  const dispatch = useAppDispatch();

  const failedStep = state.steps.find((step) => step.status === 'failed');
  const isRetryNeeded = Boolean(failedStep?.isRetryable) && !hasGoneLive;

  useEffect(() => {
    if (!isRetryNeeded) {
      if (retryAt !== undefined) dispatch(retryCancelled());
      return;
    }

    if (retryAt === undefined) {
      dispatch(retryScheduled({ now: Date.now() }));
      return;
    }

    const timer = setTimeout(() => dispatch(retryRequested()), Math.max(0, retryAt - Date.now()));
    return () => clearTimeout(timer);
  }, [dispatch, isRetryNeeded, retryAt]);
}

/**
 * Держит экран подготовки ещё немного после готовности — на «Всё готово» и уход анимацией,
 * а по истечении задержки объявляет выход в эфир.
 *
 * Раньше эта задержка была локальным `useState`, который на `!isReady` возвращался в `true`
 * — то есть чеклист был спроектирован возвращаемым. Теперь конец задержки — это событие
 * `wentLive`, и снять его может только смена матча.
 */
function useExitDelay(isReady: boolean, hasGoneLive: boolean) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isReady || hasGoneLive) return;

    const timer = setTimeout(() => dispatch(wentLive()), READINESS_EXIT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [dispatch, isReady, hasGoneLive]);
}

export default useOverlayReadiness;
